import type { PeriodSummary } from "../api/types";
import { formatDayCardTitle, formatPeriodDate, formatTemperature } from "../lib/format";

import { emptyTimeline } from "../lib/emptyTimeline";

import DayCardTimeline from "./DayCardTimeline";

export interface DayCardProps {
  period: PeriodSummary;
  onSelect?: (date: string) => void;
}

export default function DayCard({ period, onSelect }: DayCardProps) {
  const highTemp = formatTemperature(period.high.temperature);
  const lowTemp = formatTemperature(period.low.temperature);
  const tempsLabel = `${highTemp} / ${lowTemp}`;

  const header = (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[18px] font-medium text-on-surface">
          {formatDayCardTitle(period.label, period.date)}
        </p>
        <p className="text-[12px] text-on-surface-variant">{formatPeriodDate(period.date)}</p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        {onSelect ? (
          <span aria-hidden className="text-[24px] leading-none text-primary">
            ›
          </span>
        ) : null}
        <p
          className="text-[18px] font-bold tabular text-on-surface"
          aria-label={`High ${highTemp}, low ${lowTemp}`}
        >
          {tempsLabel}
        </p>
      </div>
    </div>
  );

  const body = (
    <>
      {header}
      <p className="text-[14px] text-on-surface-variant">{period.shortForecast}</p>
      <DayCardTimeline timeline={period.timeline ?? emptyTimeline()} className="mt-1" />
    </>
  );

  return (
    <article
      aria-label={`Forecast for ${period.label}. Tap to view hourly details.`}
      className="flex flex-col gap-[10px] rounded-xl bg-surface p-4 shadow"
    >
      {onSelect ? (
        <button
          type="button"
          onClick={() => onSelect(period.date)}
          className="flex w-full flex-col gap-[10px] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {body}
        </button>
      ) : (
        body
      )}
    </article>
  );
}
